import { axiosInstance } from "./api";

type TelemetryEventName = "page_view" | "gallery_view" | "artwork_view" | "gallery_performance";

export type GalleryPerformancePhase =
  | "gallery_load"
  | "model_load"
  | "model_decode"
  | "frame_sample"
  | "context_loss"
  | "quality_change"
  | "artwork_initial_budget"
  | "artwork_cleanup";

export type GalleryPerformancePayload = {
  phase: GalleryPerformancePhase;
  qualityProfile?: "LQ" | "MQ" | "HQ";
  pixelRatio?: number;
  postProcessingScale?: number;
  shadowMapSize?: number;
  galleryLoadMs?: number;
  modelLoadMs?: number;
  modelDecodeMs?: number;
  fps?: number;
  frameTimeMs?: number;
  activeAssetCount?: number;
  activeArtworkCount?: number;
  visibleArtworkCount?: number;
  pendingArtworkCount?: number;
  disposedArtworkCount?: number;
  staleArtworkCleanupCount?: number;
  estimatedTriangleCount?: number;
  estimatedTextureMb?: number;
  webglContextLost?: boolean;
  contextLossCount?: number;
  loadStrategy?: "all" | "progressive" | "preview" | "unknown";
};

type QueueableTelemetryEvent = {
  eventId: string;
  eventName: TelemetryEventName;
  occurredAt: string;
  path: string;
  anonymousId: string;
  sessionId: string;
  galleryId?: string;
  artworkId?: string;
  referrer?: string;
  mode?: string;
  gallerySpaceType?: string;
  spaceType?: string;
  spaceSubtype?: string;
  client?: Record<string, unknown>;
  performance?: GalleryPerformancePayload;
};

export type TelemetrySpaceContext = {
  gallerySpaceType?: string | null;
  spaceType?: string | null;
  spaceSubtype?: string | null;
};

const ANONYMOUS_ID_KEY = "artetra.telemetry.anonymousId";
const SESSION_ID_KEY = "artetra.telemetry.sessionId";
const MAX_BATCH_SIZE = 10;
const FLUSH_DELAY_MS = 800;
const SAFE_QUERY_PARAMS = new Set(["t", "mode", "tab", "page", "limit"]);
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const GALLERY_PERFORMANCE_PHASES = new Set<GalleryPerformancePhase>([
  "gallery_load",
  "model_load",
  "model_decode",
  "frame_sample",
  "context_loss",
  "quality_change",
  "artwork_initial_budget",
  "artwork_cleanup",
]);
const QUALITY_PROFILES = new Set(["LQ", "MQ", "HQ"]);
const LOAD_STRATEGIES = new Set(["all", "progressive", "preview", "unknown"]);

let queue: QueueableTelemetryEvent[] = [];
let flushTimer: number | null = null;
let listenersBound = false;
let volatileAnonymousId: string | null = null;
let volatileSessionId: string | null = null;

export const isTelemetryEnabled = () =>
  process.env.REACT_APP_ENABLE_TELEMETRY === "true";

const createId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getStorageValue = (
  getStorage: () => Storage,
  key: string,
  getVolatileId: () => string
) => {
  try {
    const storage = getStorage();
    const existing = storage.getItem(key);
    if (existing) {
      return existing;
    }

    const created = createId();
    storage.setItem(key, created);
    return created;
  } catch (_error) {
    return getVolatileId();
  }
};

export const getAnonymousTelemetryId = () => {
  if (!isTelemetryEnabled() || typeof window === "undefined") {
    return "";
  }

  return getStorageValue(
    () => window.localStorage,
    ANONYMOUS_ID_KEY,
    () => {
      volatileAnonymousId = volatileAnonymousId || createId();
      return volatileAnonymousId;
    }
  );
};

export const getSessionTelemetryId = () => {
  if (!isTelemetryEnabled() || typeof window === "undefined") {
    return "";
  }

  return getStorageValue(
    () => window.sessionStorage,
    SESSION_ID_KEY,
    () => {
      volatileSessionId = volatileSessionId || createId();
      return volatileSessionId;
    }
  );
};

const buildClientContext = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {};
  }

  return {
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const sanitizeTelemetryPath = (value: string) => {
  const trimmed = value.trim();
  if (
    !trimmed ||
    CONTROL_CHARACTER_PATTERN.test(trimmed) ||
    !trimmed.startsWith("/") ||
    trimmed.startsWith("//") ||
    /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
  ) {
    return "";
  }

  try {
    const parsed = new URL(trimmed, "https://artetra.local");
    const safeParams = new URLSearchParams();
    parsed.searchParams.forEach((paramValue, paramName) => {
      if (SAFE_QUERY_PARAMS.has(paramName)) {
        safeParams.append(paramName, paramValue.slice(0, 128));
      }
    });
    const query = safeParams.toString();
    return `${parsed.pathname}${query ? `?${query}` : ""}`;
  } catch (_error) {
    return "";
  }
};

const sanitizeTelemetryReferrer = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > 2048 || CONTROL_CHARACTER_PATTERN.test(trimmed)) {
    return undefined;
  }

  if (trimmed.startsWith("/")) {
    return sanitizeTelemetryPath(trimmed) || undefined;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return undefined;
    }
    const safePath = sanitizeTelemetryPath(`${parsed.pathname}${parsed.search}`);
    return safePath ? `${parsed.origin}${safePath}` : undefined;
  } catch (_error) {
    return undefined;
  }
};

const normalizeTelemetrySpaceContext = (context?: TelemetrySpaceContext) => ({
  gallerySpaceType: context?.gallerySpaceType || undefined,
  spaceType: context?.spaceType || undefined,
  spaceSubtype: context?.spaceSubtype || undefined,
});

const boundedNumber = (value: unknown, min: number, max: number) => (
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
    ? value
    : undefined
);

const boundedInteger = (value: unknown, min: number, max: number) => {
  const numberValue = boundedNumber(value, min, max);
  return Number.isInteger(numberValue) ? numberValue : undefined;
};

const assignNumber = (
  output: Record<string, unknown>,
  input: Record<string, unknown>,
  key: keyof GalleryPerformancePayload,
  min: number,
  max: number
) => {
  const value = boundedNumber(input[key], min, max);
  if (value !== undefined) {
    output[key] = value;
  }
};

const assignInteger = (
  output: Record<string, unknown>,
  input: Record<string, unknown>,
  key: keyof GalleryPerformancePayload,
  min: number,
  max: number
) => {
  const value = boundedInteger(input[key], min, max);
  if (value !== undefined) {
    output[key] = value;
  }
};

export const sanitizeGalleryPerformance = (input: unknown): GalleryPerformancePayload | null => {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return null;
  }

  const raw = input as Record<string, unknown>;
  if (!GALLERY_PERFORMANCE_PHASES.has(raw.phase as GalleryPerformancePhase)) {
    return null;
  }

  const output: Record<string, unknown> = {
    phase: raw.phase,
  };

  if (QUALITY_PROFILES.has(raw.qualityProfile as string)) {
    output.qualityProfile = raw.qualityProfile;
  }
  if (LOAD_STRATEGIES.has(raw.loadStrategy as string)) {
    output.loadStrategy = raw.loadStrategy;
  }
  if (typeof raw.webglContextLost === "boolean") {
    output.webglContextLost = raw.webglContextLost;
  }

  assignNumber(output, raw, "pixelRatio", 0, 4);
  assignNumber(output, raw, "postProcessingScale", 0, 2);
  assignInteger(output, raw, "shadowMapSize", 0, 8192);
  assignNumber(output, raw, "galleryLoadMs", 0, 600000);
  assignNumber(output, raw, "modelLoadMs", 0, 600000);
  assignNumber(output, raw, "modelDecodeMs", 0, 600000);
  assignNumber(output, raw, "fps", 0, 240);
  assignNumber(output, raw, "frameTimeMs", 0, 10000);
  assignInteger(output, raw, "activeAssetCount", 0, 10000);
  assignInteger(output, raw, "activeArtworkCount", 0, 10000);
  assignInteger(output, raw, "visibleArtworkCount", 0, 10000);
  assignInteger(output, raw, "pendingArtworkCount", 0, 10000);
  assignInteger(output, raw, "disposedArtworkCount", 0, 10000);
  assignInteger(output, raw, "staleArtworkCleanupCount", 0, 10000);
  assignInteger(output, raw, "estimatedTriangleCount", 0, 100000000);
  assignNumber(output, raw, "estimatedTextureMb", 0, 65536);
  assignInteger(output, raw, "contextLossCount", 0, 100);

  return output as GalleryPerformancePayload;
};

export const buildTelemetrySpaceContext = (
  gallery?: { spaceType?: string | null },
  space?: { type?: string | null; subtype?: string | null }
): TelemetrySpaceContext => normalizeTelemetrySpaceContext({
  gallerySpaceType: gallery?.spaceType,
  spaceType: space?.type,
  spaceSubtype: space?.subtype,
});

const flushTelemetryQueue = async () => {
  if (!isTelemetryEnabled() || queue.length === 0) {
    queue = [];
    return;
  }

  const batch = queue.splice(0, MAX_BATCH_SIZE);
  try {
    await axiosInstance.post("/analytics/events", {
      events: batch,
    });
  } catch (_error) {
    // Best-effort analytics only.
  }

  if (queue.length > 0) {
    scheduleFlush();
  }
};

const scheduleFlush = () => {
  if (flushTimer != null || typeof window === "undefined") {
    return;
  }

  flushTimer = window.setTimeout(async () => {
    flushTimer = null;
    await flushTelemetryQueue();
  }, FLUSH_DELAY_MS);
};

const bindLifecycleListeners = () => {
  if (!isTelemetryEnabled() || listenersBound || typeof window === "undefined") {
    return;
  }

  listenersBound = true;

  window.addEventListener("beforeunload", () => {
    if (queue.length === 0) {
      return;
    }

    const payload = JSON.stringify({ events: queue.slice(0, MAX_BATCH_SIZE) });
    const endpoint = `${process.env.REACT_APP_BASE_API || ""}/analytics/events`;
    try {
      navigator.sendBeacon?.(endpoint, new Blob([payload], { type: "application/json" }));
    } catch (_error) {
      // ignore
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushTelemetryQueue();
    }
  });
};

export const trackTelemetryEvent = (input: Omit<QueueableTelemetryEvent, "eventId" | "occurredAt" | "anonymousId" | "sessionId" | "client">) => {
  if (!isTelemetryEnabled() || typeof window === "undefined") {
    return;
  }

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return;
  }

  const safePath = sanitizeTelemetryPath(input.path);
  if (!safePath) {
    return;
  }
  const safeReferrer = sanitizeTelemetryReferrer(input.referrer || document.referrer);

  bindLifecycleListeners();

  queue.push({
    eventId: createId(),
    occurredAt: new Date().toISOString(),
    anonymousId: getAnonymousTelemetryId(),
    sessionId: getSessionTelemetryId(),
    client: buildClientContext(),
    ...input,
    referrer: safeReferrer,
    path: safePath,
  });

  if (queue.length >= MAX_BATCH_SIZE) {
    void flushTelemetryQueue();
    return;
  }

  scheduleFlush();
};

export const trackPageView = (path: string, mode?: string | null) => {
  trackTelemetryEvent({
    eventName: "page_view",
    path,
    mode: mode || undefined,
  });
};

export const trackGalleryView = (
  galleryId: string,
  path: string,
  mode?: string | null,
  context?: TelemetrySpaceContext
) => {
  trackTelemetryEvent({
    eventName: "gallery_view",
    path,
    galleryId,
    mode: mode || undefined,
    ...normalizeTelemetrySpaceContext(context),
  });
};

export const trackArtworkView = (
  artworkId: string,
  path: string,
  galleryId?: string,
  mode?: string | null,
  context?: TelemetrySpaceContext
) => {
  trackTelemetryEvent({
    eventName: "artwork_view",
    path,
    artworkId,
    galleryId,
    mode: mode || undefined,
    ...normalizeTelemetrySpaceContext(context),
  });
};

export const trackGalleryPerformance = (
  path: string,
  performance: GalleryPerformancePayload,
  galleryId?: string,
  mode?: string | null,
  context?: TelemetrySpaceContext
) => {
  const safePerformance = sanitizeGalleryPerformance(performance);
  if (!safePerformance) {
    return;
  }

  trackTelemetryEvent({
    eventName: "gallery_performance",
    path,
    galleryId,
    mode: mode || undefined,
    ...normalizeTelemetrySpaceContext(context),
    performance: safePerformance,
  });
};
